import React, { forwardRef } from 'react';
import MuiAlert from '@mui/material/Alert';
import { Close } from '@octanner/prism-icons';
import IconButton from '../IconButton';
import ClampText from './ClampText';
import { tannerGray } from '../ThemeProvider/colors';
import { AlertProps as MuiAlertProps } from '@mui/material/Alert';

interface AlertProps extends Omit<MuiAlertProps, 'severity' | 'variant'> {
  variant?: 'standard' | 'filled' | 'outlined' | 'vertical';
  severity?: 'error' | 'warning' | 'info' | 'success' | 'secondary';
  maxLines?: number;
}

const Alert = forwardRef<HTMLDivElement, AlertProps>(
  (
    {
      action,
      onClose,
      variant = 'standard',
      children,
      severity = 'info',
      maxLines = 3,
      ...props
    },
    ref,
  ) => {
    const secondaryStyles =
      severity === 'secondary'
        ? {
            backgroundColor: tannerGray['100'],
            color: tannerGray['700'],
            '.MuiButton-text': {
              color: tannerGray['800'],
              '&:hover': {
                color: tannerGray['800'],
                backgroundColor: tannerGray['300'],
              },
            },
            ' .MuiIconButton-root': {
              color: tannerGray['700'],
              '&:hover,&:active': {
                color: tannerGray['800'],
                backgroundColor: tannerGray['300'],
              },
            },
          }
        : {};

    return (
      <MuiAlert
        ref={ref}
        {...props}
        sx={
          variant === 'vertical'
            ? {
                display: 'flex',
                padding: '8px 16px',
                '& .MuiAlert-message': {
                  padding: '4px 0px',
                },
                alignItems: 'baseline',
                ...secondaryStyles,
              }
            : secondaryStyles
        }
        variant="standard"
        severity={severity === 'secondary' ? 'info' : severity}
        action={
          <>
            {action}
            {onClose && (
              <IconButton
                size="small"
                sx={{
                  marginLeft: '8px',
                  ...(severity === 'secondary' && {
                    '&.MuiIconButton-sizeSmall': {
                      color: tannerGray[600],
                      '&:hover': {
                        backgroundColor: tannerGray[200],
                      },
                    },
                  }),
                }}
                onClick={onClose}
                aria-label="close"
                color={severity}
              >
                <Close />
              </IconButton>
            )}
          </>
        }
      >
        {variant === 'vertical' ? (
          <ClampText severity={severity} text={children} maxLines={maxLines} />
        ) : (
          children
        )}
      </MuiAlert>
    );
  },
);

Alert.displayName = 'Alert';

export default Alert;
export * from '@mui/material/Alert';
